'use client'

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { io, Socket } from 'socket.io-client'

interface PresenceProps {
  roomId: string
  userId: string
  userName: string
  onUIAction?: (action: string, payload: any) => void
  children: React.ReactNode
}

interface CursorPosition {
  x: number // relative: 0 to 1
  y: number // relative: 0 to 1
}

interface OtherUser {
  userId: string
  userName: string
  position: CursorPosition
}

const socketUrl = 'http://localhost:3001' // Change if your server URL is different

interface PresenceContextProps {
  syncUIAction: (action: string, payload: any) => void
}

const PresenceContext = createContext<PresenceContextProps | null>(null)

export function usePresence() {
  const context = useContext(PresenceContext)
  if (!context) {
    throw new Error('usePresence must be used within a PresenceProvider')
  }
  return context
}

export default function Presence({
  roomId,
  userId,
  userName,
  onUIAction,
  children
}: PresenceProps) {
  const [otherUsers, setOtherUsers] = useState<OtherUser[]>([])
  const socketRef = useRef<Socket | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const socket = io(socketUrl)
    socketRef.current = socket

    socket.on('connect', () => {
      console.log('Connected to Socket.IO as', socket.id)
      socket.emit('joinRoom', { roomId, userId, userName })
    })

    // Listen for relative mouse moves.
    socket.on(
      'mouseMove',
      (data: {
        userId: string
        position: CursorPosition
        userName?: string
      }) => {
        setOtherUsers(prevUsers => {
          const filtered = prevUsers.filter(u => u.userId !== data.userId)
          return [
            ...filtered,
            {
              userId: data.userId,
              userName: data.userName || 'Guest',
              position: data.position
            }
          ]
        })
      }
    )

    // Listen for UI actions.
    socket.on(
      'uiAction',
      (data: { userId: string; action: string; payload: any }) => {
        if (data.userId === userId) return // ignore own events
        if (onUIAction) {
          onUIAction(data.action, data.payload)
        }
      }
    )

    return () => {
      socket.disconnect()
    }
  }, [roomId, userId, userName, onUIAction])

  // Function to sync UI actions.
  const syncUIAction = (action: string, payload: any) => {
    socketRef.current?.emit('uiAction', { roomId, userId, action, payload })
  }

  // Handle mouse move with relative coordinates.
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const relativePosition = {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height
    }
    socketRef.current?.emit('mouseMove', {
      roomId,
      userId,
      position: relativePosition,
      userName
    })
  }

  // Compute absolute position for remote cursors.
  const getAbsolutePosition = (position: CursorPosition) => {
    if (!containerRef.current) return { x: 0, y: 0 }
    const { offsetWidth: width, offsetHeight: height } = containerRef.current
    return { x: position.x * width, y: position.y * height }
  }

  return (
    <PresenceContext.Provider value={{ syncUIAction }}>
      <div
        ref={containerRef}
        style={{ position: 'relative' }}
        onMouseMove={handleMouseMove}
      >
        {children}
        {otherUsers.map(user => {
          const absolutePos = getAbsolutePosition(user.position)
          return (
            <div
              key={user.userId}
              style={{
                position: 'absolute',
                left: absolutePos.x,
                top: absolutePos.y,
                pointerEvents: 'none',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <div
                style={{
                  backgroundColor: '#21728d',
                  color: '#fff',
                  padding: '2px 4px',
                  borderRadius: 4,
                  fontSize: 10
                }}
              >
                {user.userName}
              </div>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'red',
                  margin: '0 auto'
                }}
              ></div>
            </div>
          )
        })}
      </div>
    </PresenceContext.Provider>
  )
}
